import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/shadcn/dialog";
import { Button } from "@/components/shadcn/button";

type AddressFields = {
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
};

type AddressEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: AddressFields;
  companyAddress: AddressFields;
  onSave: (fields: AddressFields) => Promise<void>;
  isMandatory?: boolean;
  isReadOnly?: boolean;
};

const inputCls = "w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const errorInputCls = "w-full rounded border border-red-500 bg-red-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const labelCls = "text-[11px] font-normal text-gray-400 uppercase tracking-wider";

export const AddressEditDialog = ({ open, onOpenChange, initial, companyAddress, onSave, isMandatory, isReadOnly }: AddressEditDialogProps) => {
  const [saving, setSaving] = useState(false);
  const [mandatoryError, setMandatoryError] = useState(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<AddressFields>({ defaultValues: initial });

  const watchedValues = watch();

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) { reset(initial); setMandatoryError(false); }
    onOpenChange(isOpen);
  };

  const copyFromCompany = () => {
    (Object.keys(companyAddress) as (keyof AddressFields)[]).forEach((k) => setValue(k, companyAddress[k]));
    setMandatoryError(false);
  };

  const onSubmit = handleSubmit(async (values) => {
    if (isMandatory && (!values.address1?.trim() || !values.postcode?.trim())) {
      setMandatoryError(true);
      return;
    }
    setSaving(true);
    try {
      await onSave(values);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  });

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Address</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          {/* Company address reference */}
          {Object.values(companyAddress).some(Boolean) && (
            <div className="mb-3 rounded bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
              <div className="flex items-center justify-between mb-1">
                <span className={labelCls}>Company address</span>
                <button type="button" className="text-xs text-blue-600 hover:underline" onClick={copyFromCompany}>
                  Copy
                </button>
              </div>
              <address className="not-italic text-gray-600 leading-snug text-xs">
                {[companyAddress.address1, companyAddress.address2, companyAddress.address3, companyAddress.address4,
                  companyAddress.town, companyAddress.county, companyAddress.postcode, companyAddress.country]
                  .filter(Boolean).map((l, i) => <span key={i} className="block">{l}</span>)}
              </address>
            </div>
          )}
          {mandatoryError && (
            <p className="text-xs text-red-500 mb-2">Address line 1 and postcode are required.</p>
          )}
          <div className="space-y-3 py-2">
            {(["address1","address2","address3","address4"] as const).map((f, i) => (
              <div key={f}>
                <label className={labelCls}>{i === 0 ? <>Address{isMandatory && <span className="text-red-500 ml-0.5">*</span>}</> : "\u00a0"}</label>
                <input {...register(f)} disabled={isReadOnly} className={mandatoryError && f === "address1" && !watchedValues.address1?.trim() ? errorInputCls : inputCls} placeholder={`Address line ${i + 1}`} />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Town</label>
                <input {...register("town")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>County</label>
                <input {...register("county")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Postcode{isMandatory && <span className="text-red-500 ml-0.5">*</span>}</label>
                <input {...register("postcode")} disabled={isReadOnly} className={mandatoryError && !watchedValues.postcode?.trim() ? errorInputCls : inputCls} />
              </div>
              <div>
                <label className={labelCls}>Country</label>
                <input {...register("country")} className={inputCls} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
